import * as React from 'react'
import { Container, Row, Col } from 'components/Layout'
import Page from 'components/Page'
import PageHeader from 'components/Page/PageHeader'
import { Crumbs, Crumb } from 'components/BreadCrumbs'
import { dispatchToProps } from './Recording.container'
import InfoCard from 'components/Cards/InfoCard'
import StickyChannel from 'components/StickyChannel'
import SimpleTable from 'components/Tables/SimpleTable'
import { moment } from 'features/Moment'
import Button from 'components/Button'
import config from 'src/config'
import LoadingOverlay from 'components/Loading/LoadingOverlay'
import { join, compact } from 'lodash'
import { Link } from 'react-router'
import { useGetRecordingQuery } from 'src/generated/graphql'

import cn from 'classnames'
import * as sAnim from 'components/Animation/Animation.scss'
import * as sLay from 'styles/modules/layout.scss'
import * as sMarg from 'styles/modules/margins.scss'
import * as sText from 'components/Text/Text.scss'

export interface IPageRecording {
  recordingId: string
  confirmDeleteRecording: typeof dispatchToProps['confirmDeleteRecording']
}

const joinAndCompact = (items: (string | undefined | null)[]) => join(compact(items), ' ')

export const Recording = (props: IPageRecording) => {
  const { recordingId, confirmDeleteRecording } = props
  const { data } = useGetRecordingQuery({ variables: { recordingId } })

  if (!data || !data.recording) {
    return (
      <Page layout={{ header: 'app' }} title="Recordings">
        <LoadingOverlay transparent />
      </Page>
    )
  }

  const recording = data.recording

  return (
    <Page layout={{ header: 'app' }} title="Recordings">
      <PageHeader>
        <Crumbs title="Recordings">
          <Crumb parent to="/dashboard/recordings">
            Recordings
          </Crumb>
          <Crumb active to={`/dashboard/recordings/${recordingId}`}>
            {recording && recording.sid.slice(0, 6)}
          </Crumb>
        </Crumbs>
      </PageHeader>
      <Container paddingTop paddingBottom className={cn('md', sAnim.comeUp)}>
        <InfoCard title="Audio" className={sMarg.mb30to15}>
          <audio controls style={{ width: '100%' }}>
            <source
              src={`${config.api.portal}/api/v1/twilio/recordings/${recording.sid}.mp3`}
              type="audio/mpeg"
            />
          </audio>
        </InfoCard>
        <Row className={cn(sLay['layout-xs-column'], sLay['layout-gt-xs-row'], 'md')}>
          <Col className={cn('md', sLay['flex-gt-xs-60'])}>
            <InfoCard title="Recording Details" className={sMarg.mb30to15}>
              <SimpleTable>
                <div>
                  <div>Recording ID:</div>
                  <div>{recording.id}</div>
                </div>
                <div>
                  <div>Created:</div>
                  <div>{moment(recording.dateCreated).format('DD MMM YYYY, h:mm:ss a')}</div>
                </div>
                <div>
                  <div>Duration:</div>
                  <div>{recording.duration}s</div>
                </div>
                <div>
                  <div>Recording Type:</div>
                  <div>
                    {recording.source === 'DialVerb' ? 'Forward Call' : 'Record Voicemail'}
                  </div>
                </div>
                <div>
                  <div>Status:</div>
                  <div style={{ textTransform: 'capitalize' }}>{recording.status}</div>
                </div>
              </SimpleTable>
            </InfoCard>
            {recording.twilioCall && (
              <InfoCard title="Caller Details" className={sMarg.mb30to15}>
                <SimpleTable>
                  <div>
                    <div>Caller Number:</div>
                    <div>{recording.twilioCall.from}</div>
                  </div>
                  {recording.twilioCall.contact && (
                    <div>
                      <div>Caller Name:</div>
                      <div>
                        <Link to={`/dashboard/contacts/${recording.twilioCall.contact.id}`}>
                          {joinAndCompact([
                            recording.twilioCall.contact.company,
                            recording.twilioCall.contact.firstName,
                            recording.twilioCall.contact.lastName,
                          ]) || 'Unnamed contact'}
                        </Link>
                      </div>
                    </div>
                  )}
                </SimpleTable>
              </InfoCard>
            )}
            {recording.transcriptions.map(transcription => (
              <InfoCard key={transcription.sid} title="Transcription" className={sMarg.mb30to15}>
                {transcription.transcriptionText}
              </InfoCard>
            ))}
            {recording.transcriptions.length === 0 && (
              <InfoCard title="Transcriptions" className={sMarg.mb30to15}>
                <div className={sText.descrip3}>
                  Did you know, recordings can be automatically transcribed to text. Simply enable
                  'Transcibe voice to text' in your Call Flow.
                </div>
              </InfoCard>
            )}
          </Col>
          <Col className={cn(sLay['flex-gt-xs-40'], 'md')}>
            <StickyChannel>
              <div className={sLay['layout-column']}>
                <Button
                  href={`${config.api.portal}/api/v1/twilio/recordings/${recording.sid}.mp3`}
                  download={`pickle-recording-${recording.id}.mp3`}
                  className={sMarg.mb15}
                  color="primary"
                  size="sm"
                  rect
                >
                  Download
                </Button>
                <Button
                  className={sMarg.mb15}
                  color="warn"
                  size="sm"
                  onClick={() => confirmDeleteRecording(recording.id)}
                  rect
                >
                  Delete Recording
                </Button>
              </div>
            </StickyChannel>
          </Col>
        </Row>
      </Container>
    </Page>
  )
}



// WEBPACK FOOTER //
// src/pages/Recording/Recording.tsx